import React from 'react';
import { IonContent, IonPage } from '@ionic/react';
import SiteNav from '../components/SiteNav';
import SiteFooter from '../components/SiteFooter';
import Reveal from '../components/Reveal';

const experiences = [
  {
    title: 'Software Engineer',
    company: 'beyonnex.io',
    period: 'Jul 2024 — Present',
    location: 'Hamburg, Germany (Remote)',
    current: true,
    description:
      'Building the cloud-native backend of an IoT energy-metering platform: Kotlin and Quarkus services on Kubernetes and AWS, ingesting live sensor data at scale.',
    achievements: [
      'Design and own scalable backend services in Kotlin/Quarkus, deployed on Kubernetes with high-availability AWS infrastructure',
      'Built end-to-end IoT ingestion pipelines connecting physical metering devices via AWS IoT Core, MQTT, and Mbus Gateway 6',
      'Drive architecture and technology decisions across the backend as part of a small senior team'
    ],
    technologies: ['Kotlin', 'Quarkus', 'Kubernetes', 'AWS', 'AWS IoT Core', 'MQTT', 'Mbus Gateway 6']
  },
  {
    title: 'Back End Developer',
    company: 'Testo SE & Co. KGaA',
    period: 'Dec 2021 — Jun 2024',
    location: 'Berlin, Germany',
    description:
      'Advanced backend systems in Java 17/21, with infrastructure automation, CI/CD and monitoring for high-uptime enterprise applications.',
    achievements: [
      'Built advanced backend systems in Java 17/21 with Spring Boot, coordinating delivery across interdisciplinary teams',
      'Spearheaded infrastructure automation with Terraform, streamlining application staging across AWS and Azure',
      'Refined database design and query performance across MySQL and DynamoDB',
      'Built CI/CD pipelines with Jenkins and GitLab CI integrated with renovate bot, cutting build times and accelerating go-live cycles',
      'Implemented monitoring with Datadog and AWS CloudWatch for high-stakes applications requiring maximum uptime'
    ],
    technologies: ['Java 17/21', 'Spring Boot', 'Terraform', 'AWS', 'Azure', 'Docker', 'Kubernetes', 'Jenkins', 'GitLab CI', 'Datadog', 'MySQL', 'DynamoDB']
  },
  {
    title: 'Back End Developer',
    company: 'evia',
    period: 'Jun 2019 — Nov 2021',
    location: 'Stuttgart, Germany',
    description:
      'Scalable Spring Boot microservices for enterprise clients, built test-first and delivered through containerised CI/CD.',
    achievements: [
      'Drove design and implementation of scalable Spring Boot microservices, applying test-driven development',
      'Delivered the Audi Consent Store, Credit Plus REST services, and the Lidl Store Support Portal',
      'Strengthened live-environment support through Docker/Kubernetes CI/CD pipelines on Jenkins'
    ],
    technologies: ['Java 11', 'Spring Boot', 'Docker', 'Kubernetes', 'Jenkins', 'AWS', 'MongoDB', 'JUnit', 'Mockito']
  },
  {
    title: 'Software Engineer',
    company: '360T',
    period: 'May 2017 — Mar 2019',
    location: 'Frankfurt, Germany',
    description:
      'Delivered software components for a complex FX market trading platform, alongside the MSc at TU Darmstadt.',
    achievements: [],
    technologies: ['Java', 'Spring Boot', 'Docker', 'Oracle', 'FX Markets']
  },
  {
    title: 'Front End Developer',
    company: 'asuro GmbH',
    period: 'Aug — Dec 2016',
    location: 'Frankfurt am Main, Germany',
    description: 'Front-end development for web applications, implementing responsive layouts with designers.',
    achievements: [],
    technologies: ['Front-end Development', 'Web Technologies']
  },
  {
    title: 'System Analyst & WebSphere Portal Developer',
    company: 'GBM / eGovernment Authority',
    period: 'Apr — Oct 2015',
    location: 'Bahrain',
    description:
      'Enterprise portal applications for Bahrain\u2019s national eGovernment portal, plus requirements analysis between stakeholders and development teams.',
    achievements: [],
    technologies: ['IBM WebSphere Portal', 'System Analysis']
  },
  {
    title: 'Application Developer',
    company: 'American Express (client engagement via Royal Cyber)',
    period: 'Apr 2013 — Jul 2014',
    location: '',
    description:
      'Improved the payment settlement experience on the Card Member Remittance System, delivering new application features for cardholders.',
    achievements: [],
    technologies: ['Java EE (EJB 3.1)', 'JSF', 'IBM WebSphere Portal', 'LDAP', 'Jasper Reports', 'SQL Server', 'Oracle']
  },
  {
    title: 'Software Engineer',
    company: 'Royal Cyber Inc',
    period: 'Sep 2011 — Dec 2014',
    location: 'Pakistan',
    description: 'Delivered enterprise-grade applications for diverse international clients on IBM enterprise technologies.',
    achievements: [
      'Developed solutions using IBM WebSphere Portlet Factory and Web Experience Factory',
      'Implemented business processes with WebSphere Lombardi and IBM BPM 7.5/8.0'
    ],
    technologies: ['IBM WebSphere', 'IBM BPM', 'WebSphere Lombardi']
  }
];

const Experience: React.FC = () => (
  <IonPage>
    <IonContent>
      <SiteNav current="/experience" />
      <main className="page">
        <Reveal>
          <p className="eyebrow"><em>cat</em> ~/experience.log</p>
          <h1 className="display" style={{ fontSize: 'clamp(1.9rem, 5vw, 3rem)' }}>
            A decade of backend engineering
          </h1>
          <p className="lede">
            Over a decade full-time since 2011 &mdash; across IoT platforms, enterprise systems and fintech &mdash; plus working-student roles while completing an MSc at TU Darmstadt.
          </p>
        </Reveal>

        <div className="timeline" style={{ marginTop: '3rem' }}>
          {experiences.map((exp, i) => (
            <Reveal key={i} delay={Math.min(i * 0.05, 0.2)}>
              <div className={`tl-item${exp.current ? ' tl-item--current' : ''}`}>
                <div className="tl-item__head">
                  <div className="tl-item__headline">
                    <h2 className="tl-item__title">{exp.title}</h2>
                    <p className="tl-item__org">{exp.company}</p>
                  </div>
                  <div className="tl-item__meta">
                    <em>{exp.period}</em>
                    {exp.location && <span>{exp.location}</span>}
                  </div>
                </div>
                <p className="tl-item__desc">{exp.description}</p>
                {exp.achievements.length > 0 && (
                  <ul className="tl-item__list">
                    {exp.achievements.map((a, j) => (
                      <li key={j}>{a}</li>
                    ))}
                  </ul>
                )}
                <div className="chips">
                  {exp.technologies.map((t, j) => (
                    <span className="chip" key={j}>{t}</span>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <SiteFooter />
      </main>
    </IonContent>
  </IonPage>
);

export default Experience;
